import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const GameContext = createContext(null);

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

export const GameProvider = ({ children }) => {
  // Current view: 'attract', 'form', 'game', 'endscreen', 'admin'
  const [currentView, setCurrentView] = useState('attract');
  const [currentLead, setCurrentLead] = useState(null);
  const [currentScore, setCurrentScore] = useState(0);
  const [endType, setEndType] = useState('timeout'); // 'timeout' or 'gameover'
  const [language, setLanguage] = useState('it');
  const [settings, setSettings] = useState(null);
  const [isTop10, setIsTop10] = useState(false);
  // Reliable ref for final score — immune to React batching/timing issues
  const finalScoreRef = useRef(0);

  const navigateTo = useCallback((view) => {
    setCurrentView(view);
  }, []);

  const startGame = useCallback((lead) => {
    setCurrentLead(lead);
    setCurrentScore(0);
    finalScoreRef.current = 0;
    setEndType('timeout');
    setCurrentView('game');
  }, []);

  const endGame = useCallback((score, type) => {
    finalScoreRef.current = score;
    setCurrentScore(score);
    setEndType(type);
    setCurrentView('endscreen');
  }, []);

  const resetToAttract = useCallback(() => {
    setCurrentLead(null);
    setCurrentScore(0);
    setEndType('timeout');
    setIsTop10(false);
    setCurrentView('attract');
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => prev === 'it' ? 'en' : 'it');
  }, []);

  const value = {
    currentView,
    currentLead,
    currentScore,
    endType,
    language,
    settings,
    isTop10,
    finalScoreRef,
    setCurrentView,
    setCurrentLead,
    setCurrentScore,
    setEndType,
    setLanguage,
    setSettings,
    setIsTop10,
    navigateTo,
    startGame,
    endGame,
    resetToAttract,
    toggleLanguage
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};

export default GameContext;
